import React, { useState, useEffect, useContext } from "react"
import { useDispatch } from "react-redux"
import { useQuery, useMutation, useQueryClient } from "react-query"
import { request, mutationRequest } from "../../util/request"
import { Spinner } from "./Spinner"
import { RequestError } from "./RequestError"
import { FollowButton } from "../shared/Button"
import { Circle, CheckCircle } from "react-bootstrap-icons"
import { openAuthDialog } from "../Authentication"
import { UserContext } from "../HomeApp"

export const FollowComponent = ({
  bandId,
  bandName,
  className = "",
  renderMode = "block",
  variant = "filled",
}) => {
  const { user } = useContext(UserContext)
  const queryClient = useQueryClient()
  const [userBandId, setUserBandId] = useState(null)
  // util/request's shared `request()` fetcher hard-codes queryKey[1] as the
  // {path, params} config (see util/request.js) - every useQuery call in
  // this app must keep that as a 2-element [identity, {path,params}] array.
  // Other components (e.g. BandScreen's auto-follow effect) invalidate this
  // by passing [queryKey] as a prefix match, not by flattening this array.
  const queryKey = ["user_band", user.id, bandId]
  const { isLoading, isError, data, error, isFetching } = useQuery(
    [
      queryKey,
      {
        path: "/api/v1/user_bands/anything.json",
        params: { band_id: bandId, user_id: user.id },
      },
    ],
    request,
  )

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setUserBandId(data?.user_band?.id)
    }
  }, [isLoading, isFetching])

  const mutation = useMutation(mutationRequest)

  const handleCreateUserBand = () => {
    mutation.mutate({
      options: { method: "POST" },
      path: `/api/v1/user_bands`,
      data: { band_id: bandId },
    })
  }

  const handleDestroyUserBand = () => {
    mutation.mutate({
      options: { method: "DELETE" },
      path: `/api/v1/user_bands/${userBandId}`,
    })
  }

  useEffect(() => {
    if (mutation.isSuccess) {
      setUserBandId(mutation.data.id)
      // queryKey is nested as the query's own first element (see the
      // useQuery call above) - wrap it so this is a prefix match, not an
      // (always-failing) exact match against the 2-element real key.
      queryClient.invalidateQueries([queryKey])
    } else if (mutation.isError) {
      console.error("Failed to create user_band relationship")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.isSuccess, mutation.isError])

  if (renderMode === "block") {
    return (
      <div>
        {isLoading ? (
          <Spinner className="text-gray-500" />
        ) : error ? (
          <RequestError error={error} />
        ) : (
          <FollowButton
            onClick={(e) => {
              if (!!userBandId) {
                handleDestroyUserBand()
              } else {
                handleCreateUserBand()
              }
            }}
            className={className}
            active={!!userBandId}
            variant={variant}
          >
            {!!userBandId ? (
              <span className="flex items-center justify-center gap-1">
                <CheckCircle width={variant === "outline" ? 16 : 20} height={variant === "outline" ? 16 : 20} />
                <span>Following</span>
              </span>
            ) : (
              <span className="flex items-center justify-center gap-1">
                <Circle width={variant === "outline" ? 16 : 20} height={variant === "outline" ? 16 : 20} />
                <span>Follow</span>
              </span>
            )}
          </FollowButton>
        )}
      </div>
    )
  } else {
    return (
      <>
        {isLoading ? (
          <></>
        ) : error ? (
          <></>
        ) : (
          <button
            onClick={(e) => {
              if (!!userBandId) {
                handleDestroyUserBand()
              } else {
                handleCreateUserBand()
              }
            }}
            className={`${className} focus:outline-none`}
          >
            {!!userBandId ? <span>Stop Following</span> : <span>Follow</span>}
          </button>
        )}
      </>
    )
  }
}

export const FollowComponentLoggedOut = ({
  bandId,
  bandName,
  className = "",
  renderMode = "block",
  variant = "filled",
}) => {
  const dispatch = useDispatch()

  if (renderMode === "block") {
    return (
      <div>
        <FollowButton
          className={className}
          active={false}
          variant={variant}
          onClick={() =>
            dispatch(
              openAuthDialog({
                bandId,
                modalState: "register",
                explanation: `Join ${bandName}'s local scene to be in the know`,
              }),
            )
          }
        >
          <span className="flex items-center justify-center gap-1">
            <Circle width={variant === "outline" ? 16 : 20} height={variant === "outline" ? 16 : 20} />
            <span>Follow</span>
          </span>
        </FollowButton>
      </div>
    )
  } else {
    return (
      <>
        <button
          onClick={() =>
            dispatch(
              openAuthDialog({
                bandId,
                modalState: "register",
                explanation: `You must first sign up or log in to follow ${bandName}`,
              }),
            )
          }
          className={`${className} focus:outline-none`}
        >
          <span>Follow</span>
        </button>
      </>
    )
  }
}
